"use client";

import { useState } from "react";
import {
  money,
  pct,
  n,
  calcIncomeRow,
  exportIncomeData,
  copyTableToClipboard,
  buildCSV,
  downloadCSV,
} from "./utils";
import { INCOME_BASE_HEADERS, INCOME_END_HEADERS } from "./constants";
import { Plus, Copy, Download, UsersRound } from "lucide-react";

export default function IncomeTab({ state }) {
  const {
    incomeRows,
    customIncomeColumns,
    annualMode,
    multiplier,
    month,
    addIncomeRow,
    updateIncomeRow,
    updateCustomIncome,
    persistIncomeRow,
    prefillIncomeRows,
    removeIncomeRow,
    addCustomIncomeColumn,
    removeCustomIncomeColumn,
    incomeSaving,
    error,
    saveMessage,
  } = state;

  const [newColumnName, setNewColumnName] = useState("");
  const [copyAlert, setCopyAlert] = useState("");

  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    const success = addCustomIncomeColumn(newColumnName.trim());
    if (success) setNewColumnName("");
  };

  const handleRemoveColumn = (key) => {
    if (!window.confirm("Remove this custom income column?")) return;
    removeCustomIncomeColumn(key);
  };

  const handleCopyTable = async () => {
    const data = exportIncomeData(incomeRows, customIncomeColumns, annualMode);
    await copyTableToClipboard(data);
    setCopyAlert("Income table copied. Paste it into Excel or Google Sheets.");
    setTimeout(() => setCopyAlert(""), 3000);
  };

  const handleDownloadCSV = () => {
    const data = exportIncomeData(incomeRows, customIncomeColumns, annualMode);
    const csv = buildCSV(data);
    downloadCSV(csv, `school-income-${month.toLowerCase()}.csv`);
  };

  const allHeaders = [
    ...INCOME_BASE_HEADERS,
    ...customIncomeColumns.map((col) => col.name),
    ...INCOME_END_HEADERS,
  ];

  const incomeKeys = [
    "tuitionIncome",
    "admissionIncome",
    "developmentIncome",
    "transportIncome",
    "examIncome",
    "activityIncome",
    "otherIncome",
    "annualIncome",
  ];

  return (
    <div className="space-y-4">
      {/* Copy alert */}
      {copyAlert && (
        <div className="bg-[#10B981] text-white px-4 py-2 rounded-xl text-sm font-semibold">
          {copyAlert}
        </div>
      )}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2 rounded-xl text-sm font-semibold">
          {error}
        </div>
      )}
      {saveMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-xl text-sm font-semibold">
          {saveMessage}
        </div>
      )}

      {/* Custom Income Columns Panel */}
      <div className="bg-white border border-[rgba(214,124,44,0.14)] rounded-2xl shadow-md p-4 flex flex-col md:flex-row justify-between gap-4 items-start">
        <div>
          <h3 className="text-lg font-bold text-[#1D3469]">
            Add Custom Income Columns
          </h3>
          <p className="text-sm text-[#6B7280] mt-1">
            Add extra income heads such as Lab Income, Smart Class Income, Sports
            Income, Hostel Income, Books Income, Security Income, etc.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {customIncomeColumns.length === 0 ? (
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-gray-500">
                No custom income columns added
              </span>
            ) : (
              customIncomeColumns.map((col) => (
                <span
                  key={col.key}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#374151]"
                >
                  {col.name}
                  <button
                    onClick={() => handleRemoveColumn(col.key)}
                    className="px-1.5 py-0.5 rounded-full bg-[#fff0ea] text-[#EF4444] text-xs font-bold"
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center flex-shrink-0">
          <input
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            placeholder="Example: Lab Income"
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
            onKeyDown={(e) => e.key === "Enter" && handleAddColumn()}
          />
          <button
            onClick={handleAddColumn}
            className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add Column
          </button>
        </div>
      </div>

      {/* Income Entry Table */}
      <div className="bg-white rounded-3xl border border-[rgba(214,124,44,0.14)] shadow-md p-6">
        <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1D3469] tracking-tight">
              Income Entry Table
            </h2>
            <p className="text-sm text-[#475467] mt-1">
              Enter class-wise or section-wise income. Enter amount collected
              directly, or leave it blank to estimate using collection %.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={prefillIncomeRows}
              disabled={incomeSaving}
              className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-1 disabled:opacity-60"
            >
              <UsersRound className="w-4 h-4" /> {incomeSaving ? "Syncing..." : "Prefill Classes"}
            </button>
            <button
              onClick={handleCopyTable}
              className="bg-[#fff3e8] text-[#c06a25] px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-1"
            >
              <Copy className="w-4 h-4" /> Copy Table
            </button>
            <button
              onClick={handleDownloadCSV}
              className="bg-[#1D3469] text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-1"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              onClick={() => addIncomeRow()}
              disabled={incomeSaving}
              className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> {incomeSaving ? "Saving..." : "Add Row"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border border-[rgba(214,124,44,0.14)] rounded-2xl">
          <table className="w-full border-collapse min-w-[1450px]">
            <thead>
              <tr>
                {allHeaders.map((header) => (
                  <th
                    key={header}
                    className="bg-[#fff3e8] text-[#8a3f09] text-xs uppercase tracking-wider font-bold px-3 py-3 text-left border-b border-[rgba(214,124,44,0.14)] sticky top-0 z-10"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {incomeRows.map((row) => {
                const calculated = calcIncomeRow(
                  row,
                  customIncomeColumns,
                  annualMode
                );
                return (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="p-2">
                      <input
                        value={row.className}
                        placeholder="Class 1"
                        onChange={(e) =>
                          updateIncomeRow(row.id, "className", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[80px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        value={row.section}
                        placeholder="A/B"
                        onChange={(e) =>
                          updateIncomeRow(row.id, "section", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[70px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.totalStudents || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "totalStudents", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[70px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.ewsStudents || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "ewsStudents", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[70px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.ewsPayPercent || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "ewsPayPercent", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[60px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.waiverStudents || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "waiverStudents", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[70px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.waiverPercent || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "waiverPercent", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[60px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    {incomeKeys.map((key) => (
                      <td key={key} className="p-2">
                        <input
                          type="number"
                          value={row[key] || ""}
                          onChange={(e) =>
                            updateIncomeRow(row.id, key, e.target.value)
                          }
                          onBlur={() => persistIncomeRow(row.id)}
                          className="w-full min-w-[80px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                        />
                      </td>
                    ))}
                    {customIncomeColumns.map((col) => (
                      <td key={col.key} className="p-2">
                        <input
                          type="number"
                          value={row.customIncome?.[col.key] || ""}
                          onChange={(e) =>
                            updateCustomIncome(row.id, col.key, n(e.target.value))
                          }
                          onBlur={() => persistIncomeRow(row.id)}
                          className="w-full min-w-[80px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                        />
                      </td>
                    ))}
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.collectionPercent || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "collectionPercent", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[70px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.amountCollected || ""}
                        placeholder="Enter actual"
                        onChange={(e) =>
                          updateIncomeRow(row.id, "amountCollected", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[90px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={row.pendingDue || ""}
                        onChange={(e) =>
                          updateIncomeRow(row.id, "pendingDue", e.target.value)
                        }
                        onBlur={() => persistIncomeRow(row.id)}
                        className="w-full min-w-[80px] border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] outline-none"
                      />
                    </td>
                    <td className="p-2 font-extrabold text-[#1D3469] whitespace-nowrap text-xs">
                      {money(calculated.billed)}
                    </td>
                    <td className="p-2 font-extrabold text-[#10B981] whitespace-nowrap text-xs">
                      {money(calculated.collected)}
                    </td>
                    <td className="p-2 font-extrabold text-[#374151] whitespace-nowrap text-xs">
                      {money(calculated.avgCollected)}
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeIncomeRow(row.id)}
                        disabled={incomeSaving}
                        className="bg-[#fff0ea] text-[#EF4444] px-3 py-1.5 rounded-lg text-xs font-semibold"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Income Summary */}
      <div className="bg-white rounded-3xl border border-[rgba(214,124,44,0.14)] shadow-md p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-[#1D3469] tracking-tight">
            Class-wise Income Summary
          </h2>
          <p className="text-sm text-[#475467] mt-1">
            See which classes are collecting well and which need follow-up.
          </p>
        </div>
        <div className="overflow-x-auto border border-[rgba(214,124,44,0.14)] rounded-2xl">
          <table className="w-full border-collapse min-w-[950px]">
            <thead>
              <tr>
                {[
                  "Class / Grade",
                  "Students",
                  "Income Billed",
                  "Amount Collected",
                  "Avg Collected Income",
                  "Pending",
                  "Concession Impact",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    className="bg-[#fff3e8] text-[#8a3f09] text-xs uppercase tracking-wider font-bold px-3 py-3 text-left border-b border-[rgba(214,124,44,0.14)]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {incomeRows.map((row) => {
                const c = calcIncomeRow(row, customIncomeColumns, annualMode);
                const status =
                  c.rate >= 90
                    ? ["Healthy", "text-[#10B981]"]
                    : c.rate >= 75
                      ? ["Needs Follow-up", "text-[#a55f09]"]
                      : ["High Risk", "text-[#EF4444]"];
                return (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="p-2 text-xs font-bold">
                      {row.className || "Unnamed"}{" "}
                      {row.section ? `- ${row.section}` : ""}
                    </td>
                    <td className="p-2 text-xs">{row.totalStudents}</td>
                    <td className="p-2 text-xs">{money(c.billed * multiplier)}</td>
                    <td className="p-2 text-xs font-bold text-[#10B981]">
                      {money(c.collected * multiplier)}
                    </td>
                    <td className="p-2 text-xs">
                      {money(c.avgCollected * multiplier)}
                    </td>
                    <td className="p-2 text-xs text-[#EF4444]">
                      {money(c.pending * multiplier)}
                    </td>
                    <td className="p-2 text-xs text-[#a55f09]">
                      {money(c.concession * multiplier)}
                    </td>
                    <td className={`p-2 text-xs font-bold ${status[1]}`}>
                      {status[0]} ({pct(c.rate)})
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
